import type { PhenotypeModel } from '@/config/uiConfig'
import type {
  ClinicalSummaryCitation,
  ClinicalSummaryReview,
  ClinicalSummaryReviewState,
} from '@/pages/order/clinicalSummaryReviewTypes'
import type { CategorizedHPOOption } from '@/types/RequisitionFormTypes'
import { highlightTone, sentenceBounds, type HighlightTone } from './clinicalSummaryEditorHighlightSpans'

export const clinicalSummaryCategoryTooltipLabels: Record<CategorizedHPOOption['category'], string> = {
  Key: 'Key phenotype',
  Additional: 'Additional phenotype',
  Excluded: 'Pertinent Negative',
}

export interface ClinicalSummaryTooltipModel {
  termLabel: string
  hpoCode: string
  categoryLabel: string
  foundAsPhrases: string[]
  /** In-range citations, one per mention in the summary. */
  mentionCount: number
  /** The sentence holding the quoted citation, split around it. */
  contextSnippet: { before: string; match: string; after: string } | null
  /** Where the quoted citation starts in the summary. */
  quotedBegin: number | null
  /** The quoted phrase is marked in the tone the summary paints it. */
  tone: HighlightTone
}

export interface BuildClinicalSummaryTooltipModelInput {
  code: string
  termLabel: string
  category: CategorizedHPOOption['category'] | null | undefined
  reviewState?: ClinicalSummaryReviewState
  summary: string
  citations: ClinicalSummaryCitation[] | undefined
  includeContextSnippet?: boolean
  /** The lab's category line; Rady's when omitted. */
  categoryLabels?: Record<CategorizedHPOOption['category'], string>
}

export type ClinicalSummaryHoverTooltipVariant = 'highlight' | 'row' | 'chip'

export type ClinicalSummaryTooltipFooter = 'found-as' | 'mentions'

export interface ClinicalSummaryTooltipLayout {
  placement: 'above' | 'below'
  /** Space between the anchor and the card. */
  gap: number
  /** Quote the citing sentence. */
  quote: boolean
  footer: ClinicalSummaryTooltipFooter
}

/**
 * Rady follows R07 (highlight: above, found-as) and R08 (row and chip: below, start-aligned, 11px,
 * mentions). SeqOne follows S02 (chip: below, start-aligned, 8px, found-as) and borrows R07 for
 * highlights, which it does not draw.
 */
export const CLINICAL_SUMMARY_TOOLTIP_LAYOUT: Record<
  PhenotypeModel,
  Record<ClinicalSummaryHoverTooltipVariant, ClinicalSummaryTooltipLayout>
> = {
  rady: {
    highlight: { placement: 'above', gap: 8, quote: false, footer: 'found-as' },
    row: { placement: 'below', gap: 11, quote: true, footer: 'mentions' },
    chip: { placement: 'below', gap: 11, quote: true, footer: 'mentions' },
  },
  seqone: {
    highlight: { placement: 'above', gap: 8, quote: false, footer: 'found-as' },
    row: { placement: 'below', gap: 8, quote: true, footer: 'found-as' },
    chip: { placement: 'below', gap: 8, quote: true, footer: 'found-as' },
  },
}

/** Longer citing sentences are cut around the quoted phrase. */
const MAX_QUOTE_LENGTH = 280

function inRange(summary: string, { begin, end }: ClinicalSummaryCitation): boolean {
  return Number.isInteger(begin) && Number.isInteger(end) && begin >= 0 && end <= summary.length && begin < end
}

/** Distinct citation texts in document order (trimmed, de-duplicated). */
export function citationMatchPhrases(summary: string, citations: ClinicalSummaryCitation[] | undefined): string[] {
  const phrases: string[] = []
  const seen = new Set<string>()
  for (const citation of citations ?? []) {
    if (!inRange(summary, citation)) continue
    const phrase = summary.slice(citation.begin, citation.end).trim()
    if (!phrase || seen.has(phrase)) continue
    seen.add(phrase)
    phrases.push(phrase)
  }
  return phrases
}

/** Lower-cased words with a plural `s` dropped, so "Seizures" and "seizure" compare equal. */
function wordSet(value: string): Set<string> {
  const words = value.toLocaleLowerCase().match(/[\p{L}\p{N}]+/gu) ?? []
  return new Set(words.map((word) => (word.length > 3 && word.endsWith('s') ? word.slice(0, -1) : word)))
}

function sameWords(left: Set<string>, right: Set<string>): boolean {
  return left.size === right.size && [...left].every((word) => right.has(word))
}

/** The citation that names the term ("generalized hypotonia" over "floppy"), else the earliest one. */
export function quotedCitation(
  summary: string,
  citations: ClinicalSummaryCitation[] | undefined,
  termLabel: string
): ClinicalSummaryCitation | null {
  const valid = (citations ?? []).filter((citation) => inRange(summary, citation)).sort((a, b) => a.begin - b.begin)
  const label = wordSet(termLabel)
  return valid.find(({ begin, end }) => sameWords(wordSet(summary.slice(begin, end)), label)) ?? valid[0] ?? null
}

/** The sentence around `citation`, cut at word boundaries with "…" only when it is very long. */
export function citingSentence(
  summary: string,
  citation: ClinicalSummaryCitation
): { before: string; match: string; after: string } {
  const bounds = sentenceBounds(summary, citation.begin, citation.end)
  let start = bounds.start
  let stop = bounds.end
  if (stop - start > MAX_QUOTE_LENGTH) {
    const context = Math.max(0, Math.floor((MAX_QUOTE_LENGTH - (citation.end - citation.begin)) / 2))
    if (citation.begin - start > context) {
      const space = summary.indexOf(' ', citation.begin - context)
      start = space >= 0 && space < citation.begin ? space + 1 : citation.begin - context
    }
    if (stop - citation.end > context) {
      const space = summary.lastIndexOf(' ', citation.end + context)
      stop = space > citation.end ? space : citation.end + context
    }
  }
  return {
    before: `${start > bounds.start ? '…' : ''}${summary.slice(start, citation.begin)}`,
    match: summary.slice(citation.begin, citation.end),
    after: `${summary.slice(citation.end, stop)}${stop < bounds.end ? '…' : ''}`,
  }
}

export function resolveClinicalSummaryCategoryLabel(
  category: CategorizedHPOOption['category'] | null | undefined,
  labels: Record<CategorizedHPOOption['category'], string> = clinicalSummaryCategoryTooltipLabels
): string {
  if (!category) return 'Uncategorized'
  return labels[category]
}

export function buildClinicalSummaryTooltipModel(
  input: BuildClinicalSummaryTooltipModelInput
): ClinicalSummaryTooltipModel {
  const { code, termLabel, category, reviewState, summary, citations, includeContextSnippet = false } = input
  const quoted = includeContextSnippet ? quotedCitation(summary, citations, termLabel) : null
  return {
    termLabel,
    hpoCode: code,
    categoryLabel: resolveClinicalSummaryCategoryLabel(category, input.categoryLabels),
    foundAsPhrases: citationMatchPhrases(summary, citations),
    mentionCount: (citations ?? []).filter((citation) => inRange(summary, citation)).length,
    contextSnippet: quoted ? citingSentence(summary, quoted) : null,
    quotedBegin: quoted?.begin ?? null,
    tone: highlightTone(reviewState, category),
  }
}

export function buildClinicalSummaryTooltipModelForTerm(
  term: Pick<CategorizedHPOOption, 'id' | 'label' | 'category'>,
  summary: string,
  review: ClinicalSummaryReview | null | undefined,
  options?: {
    includeContextSnippet?: boolean
    citations?: ClinicalSummaryCitation[]
    categoryLabels?: Record<CategorizedHPOOption['category'], string>
  }
): ClinicalSummaryTooltipModel {
  const termReview = review?.terms[term.id]
  return buildClinicalSummaryTooltipModel({
    code: term.id,
    termLabel: term.label,
    category: term.category,
    reviewState: termReview?.reviewState,
    summary,
    citations: options?.citations ?? termReview?.citations,
    includeContextSnippet: options?.includeContextSnippet,
    categoryLabels: options?.categoryLabels,
  })
}

export function buildHighlightTooltipModelForCode(
  code: string,
  summary: string,
  clinicalDiagnosis: CategorizedHPOOption[],
  review?: ClinicalSummaryReview | null
): ClinicalSummaryTooltipModel | null {
  const term = clinicalDiagnosis.find((item) => item.id === code)
  if (!term) return null
  return buildClinicalSummaryTooltipModelForTerm(term, summary, review)
}

export interface TooltipViewportPosition {
  top: number
  left: number
}

export interface TooltipAnchorRect {
  top: number
  bottom: number
  left: number
}

/** Margin kept between the card and the viewport edge. */
const VIEWPORT_MARGIN = 8

/**
 * Start-aligned with the anchor, shifted left rather than squeezed near the right edge, and flipped
 * to the other side when the preferred one has no room.
 */
export function placeTooltip(
  anchor: TooltipAnchorRect,
  layout: Pick<ClinicalSummaryTooltipLayout, 'placement' | 'gap'>,
  size: { width: number; height: number },
  viewport: { width: number; height: number }
): TooltipViewportPosition {
  const left = Math.max(VIEWPORT_MARGIN, Math.min(anchor.left, viewport.width - size.width - VIEWPORT_MARGIN))
  const aboveTop = anchor.top - layout.gap - size.height
  const belowTop = anchor.bottom + layout.gap
  const fitsAbove = aboveTop >= VIEWPORT_MARGIN
  const fitsBelow = belowTop + size.height <= viewport.height - VIEWPORT_MARGIN
  const above = layout.placement === 'above' ? fitsAbove || !fitsBelow : !fitsBelow && fitsAbove
  return { top: above ? aboveTop : belowTop, left }
}
